import type { Trip, User, Event, Comment, Photo, WaybookAccount } from '../types'

// Helper to convert snake_case to camelCase
function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

// Generic transformer for objects with snake_case keys
function transformKeys<T>(obj: any): T {
  if (!obj || typeof obj !== 'object') return obj

  if (Array.isArray(obj)) {
    return obj.map(item => transformKeys(item)) as T
  }

  const transformed: any = {}
  for (const key in obj) {
    const camelKey = toCamelCase(key)
    const value = obj[key]
    transformed[camelKey] = typeof value === 'object' && value !== null ? transformKeys(value) : value
  }
  return transformed
}

export function transformTrip(data: any): Trip {
  const transformed = transformKeys<Trip>(data)
  // Backend returns 'img' but we need 'coverImageUrl'
  if ('img' in data && !('coverImageUrl' in transformed)) {
    (transformed as any).coverImageUrl = data.img
  }
  // Admin API returns 'cover_image_url' but we need 'coverImageUrl'
  if ('cover_image_url' in data || 'coverImageUrl' in data) {
    (transformed as any).coverImageUrl = data.cover_image_url || data.coverImageUrl
  }
  return transformed
}

export function transformTrips(data: any[]): Trip[] {
  return data.map(transformTrip)
}

export function transformUser(data: any): User {
  return transformKeys<User>(data)
}

export function transformUsers(data: any[]): User[] {
  return data.map(transformUser)
}

export function transformEvent(data: any): Event {
  const transformed = transformKeys<Event>(data)
  // Backend may return 'cover_image_url' or 'cover_image', map both to 'coverImage'
  if ('cover_image_url' in data && !('coverImage' in transformed)) {
    (transformed as any).coverImage = data.cover_image_url
  }
  if ('cover_image' in data && !('coverImage' in transformed)) {
    (transformed as any).coverImage = data.cover_image
  }
  return transformed
}

export function transformEvents(data: any[]): Event[] {
  return data.map(transformEvent)
}

export function transformComment(data: any): Comment {
  return transformKeys<Comment>(data)
}

export function transformComments(data: any[]): Comment[] {
  return data.map(transformComment)
}

export function transformPhoto(data: any): Photo {
  return transformKeys<Photo>(data)
}

export function transformPhotos(data: any[]): Photo[] {
  return data.map(transformPhoto)
}

export function transformWaybookAccount(data: any): WaybookAccount {
  return transformKeys<WaybookAccount>(data)
}

export function transformWaybookAccounts(data: any[]): WaybookAccount[] {
  return data.map(transformWaybookAccount)
}

// Generic transformer for any data type
export function transformData<T>(data: any): T {
  return transformKeys<T>(data)
}

export function transformArray<T>(data: any[]): T[] {
  return data.map(item => transformKeys<T>(item))
}
